import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/components/ui/data-table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Shield,
  Smartphone,
  Mail,
  Key,
  CheckCircle2,
  XCircle,
  Copy,
  Eye,
  EyeOff,
  Smartphone as PhoneIcon,
} from 'lucide-react';
import { useToast } from '@/hooks/useToast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export default function SecurityMFAPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('settings');
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [showVerifyDialog, setShowVerifyDialog] = useState(false);
  const [setupData, setSetupData] = useState<any>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [setupType, setSetupType] = useState<'totp' | 'email_otp' | 'sms_otp'>('totp');

  // Fetch MFA settings
  const { data: mfaSettings, isLoading: settingsLoading } = useQuery({
    queryKey: ['mfa-settings'],
    queryFn: () => api.getMFASettings(),
  });

  // Fetch MFA requirements
  const { data: requirements = [] } = useQuery({
    queryKey: ['approval-mfa-requirements'],
    queryFn: () => api.getApprovalMFARequirements(),
  });

  // Fetch verification log
  const { data: verificationLog = [] } = useQuery({
    queryKey: ['mfa-verification-log'],
    queryFn: () => api.getMFAVerificationLog(),
  });

  // Setup MFA mutation
  const setupMutation = useMutation({
    mutationFn: ({ type, phone }: { type: string; phone?: string }) =>
      api.setupMFA(type, phone),
    onSuccess: (data) => {
      setSetupData(data);
      if (data.mfa_type === 'totp') {
        setShowSetupDialog(true);
      } else {
        toast({ title: `${data.mfa_type} configured`, description: data.message });
      }
      queryClient.invalidateQueries({ queryKey: ['mfa-settings'] });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Verify setup mutation
  const verifyMutation = useMutation({
    mutationFn: (code: string) => api.verifyMFASetup(code),
    onSuccess: () => {
      toast({ title: 'MFA verified successfully!' });
      setShowVerifyDialog(false);
      setShowSetupDialog(false);
      setVerificationCode('');
      setSetupData(null);
      queryClient.invalidateQueries({ queryKey: ['mfa-settings'] });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Disable MFA mutation
  const disableMutation = useMutation({
    mutationFn: (code: string) => api.disableMFA(code),
    onSuccess: () => {
      toast({ title: 'MFA disabled successfully' });
      queryClient.invalidateQueries({ queryKey: ['mfa-settings'] });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    toast({ title: 'Copied to clipboard' });
  };

  const columns = [
    {
      accessorKey: 'created_at',
      header: 'Time',
      cell: ({ row }: any) => new Date(row.created_at).toLocaleString(),
    },
    {
      accessorKey: 'mfa_type',
      header: 'Method',
      cell: ({ row }: any) => (
        <Badge variant="outline">{row.mfa_type}</Badge>
      ),
    },
    {
      accessorKey: 'verification_type',
      header: 'Type',
    },
    {
      accessorKey: 'success',
      header: 'Status',
      cell: ({ row }: any) => (
        row.success ? (
          <Badge className="bg-green-100 text-green-800">
            <CheckCircle2 className="mr-1 h-3 w-3" /> Success
          </Badge>
        ) : (
          <Badge className="bg-red-100 text-red-800">
            <XCircle className="mr-1 h-3 w-3" /> Failed
          </Badge>
        )
      ),
    },
    {
      accessorKey: 'ip_address',
      header: 'IP Address',
    },
  ];

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Shield className="h-8 w-8" />
            Security & MFA
          </h1>
          <p className="text-muted-foreground">
            Manage two-factor authentication and security settings
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="settings">MFA Settings</TabsTrigger>
          <TabsTrigger value="requirements">Approval MFA</TabsTrigger>
          <TabsTrigger value="history">Verification History</TabsTrigger>
        </TabsList>

        <TabsContent value="settings">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Current Status */}
            <Card>
              <CardHeader>
                <CardTitle>Current MFA Status</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {mfaSettings?.mfa_enabled ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
                      <CheckCircle2 className="h-8 w-8 text-green-600" />
                      <div>
                        <p className="font-medium text-green-800">MFA Enabled</p>
                        <p className="text-sm text-green-600">
                          Using {mfaSettings.mfa_type?.replace('_', ' ')}
                        </p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Type</span>
                        <Badge>{mfaSettings.mfa_type}</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Verified</span>
                        <Badge variant={mfaSettings.is_verified ? 'default' : 'destructive'}>
                          {mfaSettings.is_verified ? 'Yes' : 'No'}
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Has Backup Codes</span>
                        <Badge variant={mfaSettings.has_backup_codes ? 'default' : 'outline'}>
                          {mfaSettings.has_backup_codes ? 'Yes' : 'No'}
                        </Badge>
                      </div>
                      {mfaSettings.last_used_at && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Last Used</span>
                          <span>{new Date(mfaSettings.last_used_at).toLocaleString()}</span>
                        </div>
                      )}
                    </div>
                    <div className="pt-4 border-t">
                      <Button
                        variant="destructive"
                        className="w-full"
                        onClick={() => {
                          const code = prompt('Enter verification code to disable MFA:');
                          if (code) disableMutation.mutate(code);
                        }}
                      >
                        <XCircle className="mr-2 h-4 w-4" />
                        Disable MFA
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                      <Shield className="h-8 w-8 text-yellow-600" />
                      <div>
                        <p className="font-medium text-yellow-800">MFA Not Enabled</p>
                        <p className="text-sm text-yellow-600">
                          Enable two-factor authentication for added security
                        </p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <Button
                        className="w-full justify-start"
                        variant="outline"
                        onClick={() => {
                          setSetupType('totp');
                          setupMutation.mutate({ type: 'totp' });
                        }}
                      >
                        <Smartphone className="mr-2 h-4 w-4" />
                        Authenticator App (TOTP)
                      </Button>
                      <Button
                        className="w-full justify-start"
                        variant="outline"
                        onClick={() => {
                          setSetupType('email_otp');
                          setupMutation.mutate({ type: 'email_otp' });
                        }}
                      >
                        <Mail className="mr-2 h-4 w-4" />
                        Email OTP
                      </Button>
                      <Button
                        className="w-full justify-start"
                        variant="outline"
                        onClick={() => {
                          setSetupType('sms_otp');
                          const phone = prompt('Enter phone number:');
                          if (phone) setupMutation.mutate({ type: 'sms_otp', phone });
                        }}
                      >
                        <PhoneIcon className="mr-2 h-4 w-4" />
                        SMS OTP
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Info Card */}
            <Card>
              <CardHeader>
                <CardTitle>About MFA</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <div>
                  <h4 className="font-medium mb-2">Authenticator App (TOTP)</h4>
                  <p className="text-muted-foreground">
                    Use apps like Google Authenticator, Authy, or 1Password to generate
                    time-based one-time passwords.
                  </p>
                </div>
                <div>
                  <h4 className="font-medium mb-2">Email OTP</h4>
                  <p className="text-muted-foreground">
                    Receive a verification code via email when performing sensitive actions.
                  </p>
                </div>
                <div>
                  <h4 className="font-medium mb-2">SMS OTP</h4>
                  <p className="text-muted-foreground">
                    Receive a verification code via SMS to your registered phone number.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="requirements">
          <Card>
            <CardHeader>
              <CardTitle>Approval MFA Requirements</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                Configure which approval actions require MFA verification
              </p>
              <div className="space-y-4">
                {requirements?.map((req: any) => (
                  <div key={req.requirement_id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-4">
                      <Badge variant="outline">{req.module}</Badge>
                      <span className="font-medium">{req.action}</span>
                      {req.min_amount && (
                        <Badge variant="secondary">
                          Min: {req.min_amount.toLocaleString()}
                        </Badge>
                      )}
                    </div>
                    <Badge variant={req.require_mfa ? 'default' : 'outline'}>
                      {req.require_mfa ? 'Required' : 'Optional'}
                    </Badge>
                  </div>
                ))}
                {requirements?.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    No MFA requirements configured
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle>Verification History</CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={columns}
                data={verificationLog || []}
                isLoading={settingsLoading}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Setup Dialog */}
      <Dialog open={showSetupDialog} onOpenChange={setShowSetupDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Setup Authenticator App</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-4">
                Scan this QR code with your authenticator app:
              </p>
              <div className="bg-white p-4 rounded-lg border inline-block">
                {setupData?.qr_code_uri && (
                  <div className="text-xs font-mono break-all p-4 bg-gray-100 rounded">
                    QR Code: {setupData.qr_code_uri}
                  </div>
                )}
              </div>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-2">Or enter this secret key manually:</p>
              <div className="flex items-center gap-2">
                <Input value={setupData?.secret || ''} readOnly className="font-mono" />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handleCopyCode(setupData?.secret || '')}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Verification Code</label>
              <Input
                placeholder="Enter 6-digit code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                maxLength={6}
                className="text-center font-mono text-lg"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSetupDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => verifyMutation.mutate(verificationCode)}
              disabled={verificationCode.length !== 6}
            >
              Verify
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Verify Dialog */}
      <Dialog open={showVerifyDialog} onOpenChange={setShowVerifyDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Enter Verification Code</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Verification Code</label>
              <Input
                placeholder="Enter 6-digit code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                maxLength={6}
                className="text-center font-mono text-lg"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVerifyDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => verifyMutation.mutate(verificationCode)}
              disabled={verificationCode.length !== 6}
            >
              Verify
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
